import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vocabularyApi, api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { WordPopoverPanel } from '../components/reader/WordPopoverPanel';
import { useWordEntry } from '../shared/reader/useWordEntry';
import { toast } from 'sonner';

/**
 * /oracle-embed — full Oracle panel rendered in isolation for the iLing-FORGE
 * browser extension iframe.
 *
 * postMessage protocol
 * ────────────────────
 * Parent → iframe:
 *   { type: 'set-token',  token }
 *   { type: 'load-word',  word, normalized_form?, language_code, sentence? }
 *
 * iframe → parent:
 *   { type: 'oracle-ready' }
 *   { type: 'oracle-resize',       height }
 *   { type: 'oracle-close' }
 *   { type: 'oracle-status',       word, normalized_form, status, color }
 *   { type: 'oracle-unauthorized' }
 */

// Matches the popup's CSS colour palette so words recolour immediately on status change.
const STATUS_COLORS = {
  new:       '#60a5fa',
  learning:  '#f59e0b',
  familiar:  '#eab308',
  known:     '#22c55e',
  mastered:  '#a3e635',
};

export default function OracleEmbed() {
  const { user } = useAuth();

  const [word, setWord]                     = useState(null);
  const [normalizedForm, setNormalizedForm] = useState(null);
  const [languageCode, setLanguageCode]     = useState('');
  const [sentenceText, setSentenceText]     = useState('');

  // WordPopoverPanel controlled state
  const [noteText, setNoteText]                   = useState('');
  const [translationText, setTranslationText]     = useState('');
  const [translationSaved, setTranslationSaved]   = useState(false);

  // Two-step word loading: cache-only first, then generate if pending.
  // languageCode: '' keeps the hook stable; we always pass language explicitly to loadEntry.
  const { wordData, setWordData, wordLoading, loadEntry, cancel } = useWordEntry({ languageCode: '' });

  // Prevent note/translation from overwriting user edits on the second (enriched) pass.
  const noteSyncedRef = useRef(false);

  // ── Wrapper ref — transform trick makes position:fixed panel measurable ──
  const containerRef = useRef(null);

  // ── Notify parent of panel height changes ────────────────────────────────
  const notifyResize = useCallback(() => {
    // containerRef wraps the fixed panel via CSS transform containment,
    // so its scrollHeight reflects the panel's actual rendered height.
    const h = containerRef.current?.scrollHeight || 0;
    if (h > 0) window.parent.postMessage({ type: 'oracle-resize', height: h }, '*');
  }, []);

  useEffect(() => {
    if (!window.ResizeObserver) return;
    const ro = new ResizeObserver(notifyResize);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [notifyResize]);

  // ── Escape key → close ───────────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') window.parent.postMessage({ type: 'oracle-close' }, '*'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Auth: URL token or auth=true redirect ────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) api.defaults.headers.common['Authorization'] = `Bearer ${urlToken}`;

    if (params.get('auth') === 'true') {
      api.get('/auth/extension-token')
        .then(({ data }) => {
          try {
            // auth-bridge.js (content script on this domain) relays to background.js
            window.postMessage({ type: 'iling-auth-token', ...data }, window.location.origin);
            window.opener?.postMessage?.({ type: 'auth-token', ...data }, '*');
          } catch {}
          document.body.innerHTML = '<div style="font-family:sans-serif;padding:2rem;color:#22c55e;background:#0a0a0a;min-height:100vh;">Connected to iLing-FORGE. You can close this tab.</div>';
        })
        .catch(() => { window.location.href = '/'; });
    }
  }, []);

  // ── postMessage listener ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = ({ data }) => {
      if (!data) return;
      if (data.type === 'set-token' && data.token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }
      if (data.type === 'load-word' && data.word) {
        const norm = data.normalized_form || data.word.toLowerCase();
        const lang = data.language_code || '';
        setWord(data.word);
        setNormalizedForm(norm);
        setLanguageCode(lang);
        setSentenceText(data.sentence || '');
        setNoteText('');
        setTranslationText('');
        setTranslationSaved(false);
        noteSyncedRef.current = false;
        loadEntry(norm, false, lang);
      }
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: 'oracle-ready' }, '*');
    return () => window.removeEventListener('message', handler);
  }, [loadEntry]);

  // ── Sync note/translation from loaded word data (first pass only) ─────────
  useEffect(() => {
    if (!wordData || wordData.error || noteSyncedRef.current) return;
    noteSyncedRef.current = true;
    setNoteText(wordData.user_state?.user_note || '');
    setTranslationText(wordData.user_state?.translation || '');
  }, [wordData]);

  // ── Signal extension on fetch error (likely expired token) ───────────────
  useEffect(() => {
    if (wordData?.error && word) {
      window.parent.postMessage({ type: 'oracle-unauthorized' }, '*');
    }
  }, [wordData?.error, word]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    cancel();
    window.parent.postMessage({ type: 'oracle-close' }, '*');
  }, [cancel]);

  const handleSetStatus = useCallback(async (status) => {
    if (!word || !languageCode) return;
    try {
      await vocabularyApi.setWordStatus(normalizedForm || word, languageCode, status);
      const color = STATUS_COLORS[status] || null;
      setWordData(prev => prev ? { ...prev, user_state: { ...(prev.user_state || {}), status } } : prev);
      window.parent.postMessage({ type: 'oracle-status', word, normalized_form: normalizedForm, status, color }, '*');
    } catch { toast.error('Could not save status'); }
  }, [word, normalizedForm, languageCode, setWordData]);

  // Fetch audio as blob so the Bearer token is included; fall back to SpeechSynthesis.
  const handlePlayAudio = useCallback(async () => {
    try {
      const res = await api.get('/tts/speak', {
        params: { text: normalizedForm || word, lang: languageCode, speed: 1.0 },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch {
      if ('speechSynthesis' in window) {
        const u = new SpeechSynthesisUtterance(word);
        u.lang = languageCode;
        window.speechSynthesis.speak(u);
      }
    }
  }, [word, normalizedForm, languageCode]);

  const handleAutoTranslate = useCallback(async () => {
    if (!wordData?.lexeme_id && !word) return;
    try {
      const { data } = await vocabularyApi.autoTranslate(wordData?.lexeme_id || word, 'en');
      setTranslationText(data?.translation || data?.text || '');
    } catch { toast.error('Translation failed'); }
  }, [word, wordData]);

  const handleSaveTranslation = useCallback(async () => {
    if (!wordData?.user_state?.state_id) return;
    try {
      await vocabularyApi.addTranslation(wordData.user_state.state_id, translationText);
      setTranslationSaved(true);
      toast.success('Translation saved');
    } catch { toast.error('Could not save translation'); }
  }, [wordData, translationText]);

  const handleSaveNote = useCallback(async (noteString) => {
    const text = typeof noteString === 'string' ? noteString : noteText;
    if (!word || !languageCode) return;
    try {
      await vocabularyApi.saveUserNote(normalizedForm || word, languageCode, text);
      toast.success('Note saved');
    } catch { toast.error('Could not save note'); }
  }, [word, normalizedForm, languageCode, noteText]);

  const handleWordLookup = useCallback((lookupWord) => {
    const norm = lookupWord.toLowerCase();
    setWord(lookupWord);
    setNormalizedForm(norm);
    setSentenceText('');
    noteSyncedRef.current = false;
    loadEntry(norm, false, languageCode);
  }, [languageCode, loadEntry]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // ── Render ────────────────────────────────────────────────────────────────
  if (!word && !wordLoading) {
    return (
      <div className="flex items-center justify-center bg-background text-foreground" style={{ height: '100vh' }}>
        <p className="text-sm text-muted-foreground">Click a word to open the Oracle.</p>
      </div>
    );
  }

  // CSS transform containment: makes the position:fixed panel measurable by the
  // ResizeObserver on containerRef, and restores normal scroll/expand-tab behaviour.
  return (
    <div
      ref={containerRef}
      className="bg-background text-foreground"
      style={{ height: '100vh', overflowY: 'auto', transform: 'translateZ(0)', position: 'relative' }}
    >
      <WordPopoverPanel
        style={{ position: 'relative', width: '100%', borderRadius: 0, boxShadow: 'none', border: 'none' }}
        wordData={wordData}
        wordLoading={wordLoading}
        token={word ? { surface_form: word, normalized_form: normalizedForm || word, is_word: true } : null}
        language={languageCode}
        sentenceText={sentenceText}
        noteText={noteText}
        setNoteText={setNoteText}
        translationText={translationText}
        setTranslationText={setTranslationText}
        translationSaved={translationSaved}
        onClose={handleClose}
        onPlayAudio={handlePlayAudio}
        onSetStatus={handleSetStatus}
        onAutoTranslate={handleAutoTranslate}
        onSaveTranslation={handleSaveTranslation}
        onSaveNote={handleSaveNote}
        onWordLookup={handleWordLookup}
        isMobile={isMobile}
        isAdmin={user?.is_admin || false}
        userTier={user?.tier || 1}
        isTTSSupported={true}
      />
    </div>
  );
}
